import React from 'react';

interface CurrencyBadgeProps {
  type: 'GC' | 'SC';
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'white';
  className?: string;
}

export const CurrencyBadge: React.FC<CurrencyBadgeProps> = ({ 
  type, 
  size = 'md',
  variant = 'default',
  className = '' 
}) => {
  const sizeClasses = {
    sm: 'w-6 h-6 text-[8px]',
    md: 'w-8 h-8 text-[10px]',
    lg: 'w-10 h-10 text-xs'
  };

  const isGC = type === 'GC';
  
  const colorClasses = variant === 'white'
    ? `bg-white ${isGC ? 'text-amber-600' : 'text-emerald-600'}`
    : isGC 
      ? 'bg-gradient-to-br from-amber-400 via-yellow-500 to-amber-600 text-amber-900' 
      : 'bg-gradient-to-br from-green-400 via-emerald-500 to-green-600 text-white';
  
  return (
    <div 
      className={`
        ${sizeClasses[size]} 
        rounded-lg 
        flex items-center justify-center 
        font-black tracking-tight
        shadow-md
        flex-shrink-0
        ${colorClasses}
        ${className}
      `}
    >
      {type}
    </div>
  );
};

// SVG versions if needed for more control
export const GCBadgeSVG: React.FC<{ size?: number; className?: string }> = ({ 
  size = 32, 
  className = '' 
}) => {
  const gradientId = React.useId();
  return (
    <svg 
      width={size} 
      height={size} 
      viewBox="0 0 32 32" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <defs>
        <linearGradient id={`gcGradient-${gradientId}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FBBF24" />
          <stop offset="50%" stopColor="#EAB308" />
          <stop offset="100%" stopColor="#D97706" />
        </linearGradient>
      </defs>
      <rect width="32" height="32" rx="6" fill={`url(#gcGradient-${gradientId})`} />
      <text 
        x="16" 
        y="20" 
        textAnchor="middle" 
        fill="#78350F" 
        fontSize="12" 
        fontWeight="900" 
        fontFamily="system-ui, -apple-system, sans-serif"
      >
        GC
      </text>
    </svg>
  );
};

export const SCBadgeSVG: React.FC<{ size?: number; className?: string }> = ({ 
  size = 32, 
  className = '' 
}) => {
  const gradientId = React.useId();
  return (
    <svg 
      width={size} 
      height={size} 
      viewBox="0 0 32 32" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <defs>
        <linearGradient id={`scGradient-${gradientId}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#4ADE80" />
          <stop offset="50%" stopColor="#10B981" />
          <stop offset="100%" stopColor="#059669" />
        </linearGradient>
      </defs>
      <rect width="32" height="32" rx="6" fill={`url(#scGradient-${gradientId})`} />
      <text 
        x="16" 
        y="20" 
        textAnchor="middle" 
        fill="white" 
        fontSize="12" 
        fontWeight="900" 
        fontFamily="system-ui, -apple-system, sans-serif"
      >
        SC
      </text>
    </svg>
  );
};

// Compact circular coin for use in sliders/compact UI
export const SliderCoin: React.FC<{ 
  type: 'GC' | 'SC'; 
  size?: number; 
  className?: string;
  active?: boolean;
}> = ({ 
  type, 
  size = 22, 
  className = '',
  active = false
}) => {
  const isGC = type === 'GC';
  
  return (
    <div 
      className={`
        flex-shrink-0 rounded-full flex items-center justify-center
        font-black text-[7px] tracking-tight
        transition-all duration-200
        ${isGC 
          ? active 
            ? 'bg-amber-900/90 text-amber-300 ring-1 ring-amber-300/50' 
            : 'bg-gradient-to-br from-amber-400 to-amber-600 text-amber-900 shadow-sm'
          : active
            ? 'bg-emerald-900/90 text-emerald-300 ring-1 ring-emerald-300/50'
            : 'bg-gradient-to-br from-emerald-400 to-emerald-600 text-white shadow-sm'
        }
        ${className}
      `}
      style={{ width: size, height: size }}
    >
      {type}
    </div>
  );
};
